// Quais cartões cada coluna mostra — inclusive o cartão PARCIAL.
//
// A regra que este arquivo carrega (pedido do dono): marcar um produto como
// pronto NÃO tira ele da comanda de origem. O produto continua lá, opaco, com
// o selo de pronto, e ao mesmo tempo aparece em Prontos num cartão só com o
// que já saiu — a "comanda separada". O bartender que fez três caipirinhas de
// três comandas diferentes vê as três em Prontos sem que nenhuma das comandas
// tenha ido embora da cozinha.

import { chaveDaComanda } from "./envios";
import { cancelamentosPendentes, ehDeMesa, itensCancelados, itensVivos } from "./kds";

/** Itens que esta tela deve mostrar: sem os riscados e, com setor escolhido,
 *  os daquele setor MAIS os que não têm setor nenhum.
 *
 *  Produto sem setor aparece em todas as telas (decisão do dono, 23/09/2026):
 *  antes ele sumia de toda tela filtrada e só existia em "Todas as praças" —
 *  numa cozinha que só usa telas filtradas, ninguém fazia (caso do Del Valle
 *  no #10755). Ele vem marcado com `_semSetorAqui` pra o cartão mostrar a
 *  etiqueta "sem setor" e alguém corrigir o cadastro da categoria. */
export function itensDoSetor(pedido, setorFilter) {
  const vivos = itensVivos(pedido);
  if (setorFilter === "todos") return vivos;
  return vivos
    .filter((i) => i.setor_id === setorFilter || !i.setor_id)
    .map((i) => (i.setor_id ? i : { ...i, _semSetorAqui: true }));
}

/** Os deletados na mesa que esta tela mostraria (mesma regra de setor). */
function canceladosDoSetor(pedido, setorFilter) {
  const todos = itensCancelados(pedido);
  if (setorFilter === "todos") return todos;
  return todos.filter((i) => i.setor_id === setorFilter || !i.setor_id);
}

/** "+2 itens · Cozinha" — o que o filtro de setor escondeu desta comanda.
 *  Sem isso o bartender não tem como saber que a mesa tem comida vindo. */
function resumoOcultos(pedido, setorFilter, setores) {
  if (setorFilter === "todos") return null;
  // Sem setor não conta como escondido: ele aparece aqui também.
  const fora = itensVivos(pedido).filter((i) => i.setor_id && i.setor_id !== setorFilter);
  if (!fora.length) return null;
  const nomes = [...new Set(fora.map((i) => {
    const s = (setores || []).find((x) => x.id === i.setor_id);
    return s ? s.name : "outra praça";
  }))];
  return { qtd: fora.length, setores: nomes };
}

/** Identidade do CARTÃO, não do pedido: a mesma comanda pode estar desenhada
 *  duas vezes (inteira em Produção, parcial em Prontos), e as duas precisam
 *  de chave e número próprios. E o mesmo pedido de mesa vira um cartão por
 *  envio do garçom (lib/envios.js). */
export function cardId(pedido, parcial) {
  return `${chaveDaComanda(pedido)}${parcial ? ":parcial" : ""}`;
}

/** Maior tempo de produção entre os setores dos produtos do cartão (os
 *  cancelados não contam) — estica a régua de cor do cartão (lib/kds.js). */
function tempoMaximo(itens, tempoSetor) {
  let max = null;
  for (const i of itens || []) {
    if (i._cancelado) continue;
    const t = tempoSetor[i.setor_id];
    if (t != null && (max == null || t > max)) max = t;
  }
  return max;
}

export function montarColunas(pedidos, { setorFilter = "todos", setores = [], agora = Date.now() } = {}) {
  const producao = [];
  const prontos = [];
  const tempoSetor = {};
  for (const s of setores || []) {
    if (s.tempo_producao != null && s.tempo_producao !== "") tempoSetor[s.id] = Number(s.tempo_producao);
  }

  for (const p of pedidos || []) {
    const visiveis = itensDoSetor(p, setorFilter);
    const cancelados = canceladosDoSetor(p, setorFilter);
    // Comanda sem nenhum item do meu setor não é problema meu — a não ser que
    // tudo tenha sido cancelado na mesa: fica até a cozinha confirmar que viu.
    if (!visiveis.length) {
      const pendentes = p.status !== "pronto" ? cancelamentosPendentes(cancelados, agora) : [];
      if (pendentes.length) {
        producao.push({ ...p, _itens: pendentes, _soCancelados: true, _confirmarCancelamento: true, _cardId: cardId(p, false) });
      }
      continue;
    }
    const ocultos = resumoOcultos(p, setorFilter, setores);
    const base = { ...p, _ocultos: ocultos };

    // Em Prontos, pedido de mesa só mostra o que ainda não chegou na mesa:
    // entregue já saiu da mão da cozinha. Tudo entregue, o cartão some.
    const aindaNaCozinha = (i) => !(ehDeMesa(p) && i.entregue_at);

    if (p.status === "pronto") {
      const esperando = visiveis.filter(aindaNaCozinha);
      if (esperando.length) prontos.push({ ...base, _itens: esperando, _cardId: cardId(p, false) });
      continue;
    }

    // A fila olha só o que ESTA TELA mostra. Com setor escolhido, a comanda
    // sai da fila quando o que é do setor ficou pronto, mesmo que outra praça
    // ainda esteja trabalhando nela (caso #10755: a Coca pronta seguia "em
    // produção" na tela de Bebidas porque o status era o da comanda inteira).
    const faltam = visiveis.filter((i) => !i.pronto_at);
    if (faltam.length) {
      // O que já saiu desce pro fim: quem olha a comanda vê primeiro o que falta
      // e, logo abaixo, o que foi cancelado na mesa (riscado, "não fazer").
      const feitos = visiveis.filter((i) => i.pronto_at);
      producao.push({ ...base, _itens: [...faltam, ...cancelados, ...feitos], _cardId: cardId(p, false) });
    }

    const jaSairam = visiveis.filter((i) => i.pronto_at && aindaNaCozinha(i));
    if (jaSairam.length) {
      prontos.push({
        ...base, _itens: jaSairam, _parcial: true, _cardId: cardId(p, true),
        _totalDaComanda: visiveis.length, _setorCompleto: faltam.length === 0,
      });
    }
  }
  for (const card of [...producao, ...prontos]) card._tempoMax = tempoMaximo(card._itens, tempoSetor);
  return { producao, prontos };
}

/** Os itens que dá pra SELECIONAR num cartão (toque ou teclado), na ordem em
 *  que aparecem — o subnúmero de cada um é a posição aqui, começando em 1.
 *
 *  Só os que ainda não saíram: o pronto não se seleciona, se desfaz pelo botão
 *  dele. Só a fila da cozinha: em Prontos a comanda pode ter subido inteira
 *  pelo botão (sem marca em item nenhum), e o cartão parcial é um espelho do
 *  que já saiu, não um lugar de marcar. */
export function itensSelecionaveis(card) {
  if (!card || card._parcial || card._soCancelados || card.status !== "producao") return [];
  return (card._itens || []).filter((i) => !i.pronto_at && !i._cancelado);
}
